/**
 * Centralised reCAPTCHA v3 wiring.
 *
 * - `<AppReCaptchaProvider>` wraps the app at App.js. If
 *   `REACT_APP_RECAPTCHA_SITE_KEY` is missing (local dev), it renders children
 *   unchanged so the site keeps working without a key.
 * - `useRecaptchaToken(action)` returns an async function that fetches a v3
 *   token. If the reCAPTCHA script is still loading, it waits up to 8 s for
 *   `executeRecaptcha` to become available before giving up — this prevents the
 *   "Missing reCAPTCHA token" error users hit when they click submit before
 *   the script had a chance to hydrate.
 */
import { useCallback } from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const READY_TIMEOUT_MS = 8000;
const POLL_INTERVAL_MS = 150;

export function AppReCaptchaProvider({ children }) {
  if (!SITE_KEY) return children;
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={SITE_KEY}
      scriptProps={{ async: true, defer: true, appendTo: "head" }}
      container={{ parameters: { badge: "bottomright" } }}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
}

/**
 * Wait until `window.grecaptcha.execute` is defined OR the timeout elapses.
 * Some browsers (or blocked-tracker environments) load the script very late.
 */
function _waitForGrecaptcha(timeoutMs) {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (typeof window !== "undefined" && window.grecaptcha && window.grecaptcha.execute) {
        return resolve(true);
      }
      if (Date.now() - start >= timeoutMs) return resolve(false);
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
  });
}

/**
 * `grecaptcha.execute` MUST be called after `grecaptcha.ready` resolves,
 * otherwise Google's script throws "reCAPTCHA has not been loaded". Wrap
 * the ready callback in a promise so we can await it cleanly.
 */
function _grecaptchaReady() {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.grecaptcha) return resolve(false);
    if (typeof window.grecaptcha.ready === "function") {
      window.grecaptcha.ready(() => resolve(true));
    } else {
      // Enterprise / already-ready builds — just resolve.
      resolve(true);
    }
  });
}

export function useRecaptchaToken() {
  const ctx = useGoogleReCaptcha() || {};
  return useCallback(
    async (action) => {
      if (!SITE_KEY) return null;
      // Hook context executeRecaptcha may be undefined for a few ms after mount.
      let exec = ctx.executeRecaptcha;
      if (!exec) {
        // Wait for grecaptcha to load, then retry reading the hook's function.
        // If the hook function is still unavailable, fall back to a direct
        // window.grecaptcha.execute call so the user isn't blocked.
        const ready = await _waitForGrecaptcha(READY_TIMEOUT_MS);
        if (!ready) {
          if (process.env.NODE_ENV === "development") console.debug("reCAPTCHA script did not load in time");
          return null;
        }
        exec = ctx.executeRecaptcha; // may have hydrated during the wait
      }
      try {
        if (typeof exec === "function") {
          return await exec(action);
        }
        // Absolute fallback: talk directly to window.grecaptcha. Google's docs
        // require grecaptcha.ready() to resolve before execute() is safe.
        await _grecaptchaReady();
        return await window.grecaptcha.execute(SITE_KEY, { action });
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.debug("reCAPTCHA execute failed", e);
        return null;
      }
    },
    [ctx.executeRecaptcha]
  );
}

export const RECAPTCHA_ENABLED = !!SITE_KEY;
